import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import moment from "moment";
import * as yup from "yup";

import pathnames from "routes/pathnames";

import api from "services/api";

import formatDateTime from "common/format-date-time";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import ERRORS from "constants/errors";
import CONSTANTS from "constants/constant";
import { statusOptions } from "constants/status-options";
import { priorityOptions } from "constants/priority-options";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import { PagePopupAdsTableDto } from "dto/pages/popup-ads/page-popup-ads-dto";

import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppHeading from "components/app-heading";
import AppTextarea from "components/app-textarea";
import AppBreadcrumbs from "components/app-breadcrumbs";
import AppSelectInput from "components/app-select-input";

const PagePopupAdsDetails = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { date } = formatDateTime(String(new Date()));
	const isAddNew = useMemo(() => (id === ActionEnum.Create ? true : false), [id]);
	const buttonLabel = useMemo(() => (isAddNew ? "Add" : "Save"), [isAddNew]);

	//prettier-ignore
	const breadcrumbItems = useMemo(() => [
        { label: "Pop-up Ads", href: pathnames.popupAds },
        { label: isAddNew ? "Add New" : "Edit Pop-up Ads", href: isAddNew ? pathnames.popupAds + "/create" : `${pathnames.popupAds}/${id}` },
    ], [id, isAddNew]);

	//prettier-ignore
	const validationSchema = yup.object({
		headline: yup.string().required(ERRORS.REQUIRED),
		status: yup.string().required(ERRORS.REQUIRED),
		priority: yup.number().required(ERRORS.REQUIRED),
		startAt: yup.string().required(ERRORS.REQUIRED).test("valid-date", ERRORS.DATE_FORMAT, (value) => {
      		return moment(value, CONSTANTS.LISTING_DATE_FORMAT, true).isValid();
    	}).test("date-range", ERRORS.INVALID_START_DATE, function (value) {
			const { endAt } = this.parent; 
			if (!value || !endAt) return true; 

			const startDate = moment(value, CONSTANTS.LISTING_DATE_FORMAT);
			const endDate = moment(endAt, CONSTANTS.LISTING_DATE_FORMAT);
			return startDate.isSameOrBefore(endDate);
		}),
		endAt: yup.string().required(ERRORS.REQUIRED).test("valid-date", ERRORS.DATE_FORMAT, (value) => {
			return moment(value, CONSTANTS.LISTING_DATE_FORMAT, true).isValid();
		}).test("date-range", ERRORS.INVALID_END_DATE, function (value) {
			const { startAt } = this.parent; 
			if (!value || !startAt) return true; 
			
			const startDate = moment(startAt, CONSTANTS.LISTING_DATE_FORMAT);
			const endDate = moment(value, CONSTANTS.LISTING_DATE_FORMAT);
			return endDate.isSameOrAfter(startDate);
		}),
		publicationDate: yup.string().required(ERRORS.REQUIRED).test("valid-date", ERRORS.DATE_FORMAT, (value) => {
			return moment(value, CONSTANTS.LISTING_DATE_FORMAT, true).isValid();
		}),
		cover:  yup.string().required(ERRORS.REQUIRED),
		title: yup.string().max(90).required(ERRORS.REQUIRED),
		description: yup.string().required(ERRORS.REQUIRED),
	});

	const formik = useFormik<PagePopupAdsTableDto>({
		initialValues: { headline: "", id: "", title: "", cover: "", description: "", status: "", startAt: date, endAt: date, publicationDate: date, priority: 0 },
		validationSchema,
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});

	//prettier-ignore
	const onHandleSubmit = useCallback(async (values: PagePopupAdsTableDto) => {
        let response = null;
 
        try {
            const payload = {
				headline: values?.headline || "",
                status: values?.status || "",
				priority: Number(values?.priority),
                startAt: moment(values?.startAt, CONSTANTS.LISTING_DATE_FORMAT).toISOString() || "",
                endAt: moment(values?.endAt, CONSTANTS.LISTING_DATE_FORMAT).toISOString() || "",
				publicationDate: moment(values?.publicationDate, CONSTANTS.LISTING_DATE_FORMAT).toISOString() || "",
                cover: values?.cover || "",
                title: values?.title || "",
				description: values.description || "",
            };
 
            response = !isAddNew && id ? await api.put.announcement.update({id: id}, payload) : await api.post.announcement.create(payload);
        } catch (error) {
            const err = error as Error | ErrorResponseDto;
 
            serveRequestErrors(err);
        }
 
        if (response) {
            navigate(pathnames.popupAds);

            if (isAddNew) serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);
            else serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
        }
     }, [navigate, id, isAddNew]);

	const onHandleGetDetails = useCallback(async () => {
		try {
			const response = await api.get.announcement.announcement({ id: id! });

			const adsDetails = response.data.data;

			formik.setValues({
				headline: adsDetails.headline || "",
				id: adsDetails.id || "",
				title: adsDetails.title,
				cover: adsDetails.cover,
				description: adsDetails.description || "",
				startAt: adsDetails.startAt ? moment(adsDetails.startAt).format(CONSTANTS.LISTING_DATE_FORMAT) : "",
				endAt: adsDetails.endAt ? moment(adsDetails.endAt).format(CONSTANTS.LISTING_DATE_FORMAT) : "",
				publicationDate: adsDetails.publicationDate ? moment(adsDetails.publicationDate).format(CONSTANTS.LISTING_DATE_FORMAT) : "",
				status: adsDetails.status || "",
				priority: adsDetails.priority ?? 0,
			});
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, formik.setValues]);

	useEffect(() => {
		if (!isAddNew) onHandleGetDetails();
	}, [isAddNew, onHandleGetDetails]);

	return (
		<div className="page-popup-ads">
			<div className="ads">
				<AppHeading title="Pop-up Ads">
					<AppHeading.Divider />
				</AppHeading>

				<form className="ads__container" onSubmit={formik.handleSubmit}>
					<div className="ads__header">
						<AppBreadcrumbs items={breadcrumbItems} />

						<div className="ads__button-wrapper">
							<AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
						</div>
					</div>

					<div className="form">
						<p className="form__title">Ads info</p>

						<div className="form__wrapper">
							{/* prettier-ignore */}
							<div className="full-width">
							    <AppInput type="text" name="headline" label="Ads Headline" placeholder="Enter Headline" value={formik.values.headline} touched={formik.touched.headline} error={formik.errors.headline} onChange={formik.handleChange} required maxLength={100}/>
							</div>

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="status" label="Status" placeholder="Please select" value={formik.values.status} touched={formik.touched.status} error={formik.errors.status} options={statusOptions} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="priority" label="Priority" placeholder="Please select" value={String(formik.values.priority)} touched={formik.touched.priority} error={formik.errors.priority} options={priorityOptions} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="startAt" label="Start Date" value={formik.values.startAt} touched={formik.touched.startAt} error={formik.errors.startAt} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="endAt" label="End Date" value={formik.values.endAt} touched={formik.touched.endAt} error={formik.errors.endAt} onChange={formik.handleChange} required />

							<div className="full-width">
								{/* prettier-ignore */}
								<AppInput type="text" name="publicationDate" label="Date of the news" value={formik.values.publicationDate} touched={formik.touched.publicationDate} error={formik.errors.publicationDate} onChange={formik.handleChange} required />
							</div>

							{/* prettier-ignore */}
							<div className="full-width">
								<AppInput type="text" name="title" label="Title" placeholder="Enter Title" value={formik.values.title} touched={formik.touched.title} error={formik.errors.title} onChange={formik.handleChange} maxLength={90} required />
							</div>

							{/* prettier-ignore */}
							<div className="full-width">
							    <AppInput type="text" name="cover" label="Cover URL" placeholder="Enter URL" value={formik.values.cover} touched={formik.touched.cover} error={formik.errors.cover} onChange={formik.handleChange} required tooltipContent="Please upload a square-sized cover image (e.g., 500x500px)" />
							</div>

							{/* prettier-ignore */}
							<div className="full-width">
								<AppTextarea name="description" label="Description" placeholder="Enter description" value={formik.values.description} touched={formik.touched.description} error={formik.errors.description} onChange={(e) => formik.setFieldValue("description", e.target.value)} rows={5} maxRows={5} maxCharLength={250} required />
							</div>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default PagePopupAdsDetails;
